import { useMemo } from 'react';
import { useDashboard } from '../../hooks/useDashboard';
import { useCache } from '../../hooks/useCache';
import { fetchFootfall, fetchSOP } from '../../api';
import { Skeleton } from './Skeleton';
import styles from './KpiGrid.module.css';

/* ── Seeded sparkline ── */
function seededRand(seed, i) {
  const x = Math.sin(seed * 127 + i * 31) * 43758;
  return x - Math.floor(x);
}

function Sparkline({ seed, color, inverted = false }) {
  const W = 100, H = 32, PAD = 3;
  const raw = Array.from({ length: 7 }, (_, i) => {
    const r = seededRand(seed + i * 17, i * 9 + 3);
    const trend = (i / 6) * (inverted ? -0.25 : 0.25);
    return Math.max(0.08, Math.min(0.92, 0.38 + r * 0.48 + trend));
  });
  const pts = raw.map((v, i) => [
    PAD + (i / 6) * (W - PAD * 2),
    (H - PAD) - v * (H - PAD * 2),
  ]);
  const polyline = pts.map(p => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const area     = `${PAD},${H - PAD} ${polyline} ${W - PAD},${H - PAD}`;
  const [lx, ly] = pts[pts.length - 1];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} fill="none" className={styles.sparkline}>
      <polygon points={area} fill={color} opacity="0.12"/>
      <polyline points={polyline} stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx={lx.toFixed(1)} cy={ly.toFixed(1)} r="2.5" fill={color} stroke="white" strokeWidth="1.2"/>
    </svg>
  );
}

function fmtHour(h) {
  const hh = Math.floor(h), mm = Math.round((h - hh) * 60);
  const ampm = hh >= 12 ? 'PM' : 'AM';
  const hd = hh > 12 ? hh - 12 : hh === 0 ? 12 : hh;
  return `${hd}:${String(mm).padStart(2, '0')} ${ampm}`;
}

export default function KpiGrid() {
  const { kpis, datesParam, startDate, endDate, visibleStores } = useDashboard();

  const ffKey  = kpis.storeIds ? `kpi-ff:${kpis.storeIds}:${datesParam}` : null;
  const sopKey = kpis.storeIds ? `kpi-sop:${kpis.storeIds}:${startDate}:${endDate}` : null;

  const { data: ffData, loading: ffLoading }  = useCache(ffKey,  () => fetchFootfall(kpis.storeIds, datesParam));
  const { data: sopData, loading: sopLoading } = useCache(sopKey, () => fetchSOP(kpis.storeIds, startDate, endDate));

  // A response counts as "no data" when it's null/missing or the backend
  // reported an error — in those cases we show an em dash, not a misleading 0.
  const ffOk  = !!ffData  && ffData.status  !== 'error';
  const sopOk = !!sopData && sopData.status !== 'error';

  const footfall = ffData?.totalFootfall || 0;
  const dwell    = ffData?.avgDwellTime  || 0;
  const isLive   = ffData?.isLive        || false;
  const sopCount = (sopData?.hygiene?.totalBreaches || 0) + (sopData?.ppe?.totalNonCompliant || 0);

  // Store Operations: avg open/close across visible stores
  const { avgOpen, avgClose } = useMemo(() => {
    if (!visibleStores.length) return { avgOpen: '--', avgClose: '--' };
    const opens  = visibleStores.filter(s => s.open).map(s => {
      const [h, m] = s.open.split(':').map(Number);
      return h + m / 60;
    });
    const closes = visibleStores.filter(s => s.close).map(s => {
      const [h, m] = s.close.split(':').map(Number);
      return h + m / 60;
    });
    const avgO = opens.length  ? opens.reduce((a, b) => a + b, 0)  / opens.length  : null;
    const avgC = closes.length ? closes.reduce((a, b) => a + b, 0) / closes.length : null;
    return {
      avgOpen:  avgO != null ? fmtHour(avgO)  : '--',
      avgClose: avgC != null ? fmtHour(avgC)  : '--',
    };
  }, [visibleStores]);

  return (
    <div className={styles.grid}>
      {/* Total Footfall */}
      <div className={styles.card}>
        <div className={styles.icon}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        </div>
        <div className={styles.label}>Total Footfall</div>
        <div className={styles.valRow}>
          {ffLoading
            ? <Skeleton width={80} height={28} />
            : <div className={styles.value}>{ffOk ? footfall.toLocaleString() : '—'}</div>}
          <span className={`${styles.badge} ${styles.up}`}>{isLive ? 'Live' : startDate}</span>
        </div>
        <Sparkline seed={1001} color="#00A3FF" />
      </div>

      {/* Avg Dwell Time */}
      <div className={styles.card}>
        <div className={styles.icon}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
        </div>
        <div className={styles.label}>Avg Dwell Time</div>
        <div className={styles.valRow}>
          {ffLoading
            ? <Skeleton width={70} height={28} />
            : <div className={styles.value}>{ffOk ? <>{dwell} <span className={styles.unit}>min</span></> : '—'}</div>}
          <span className={`${styles.badge} ${styles.up}`}>avg across stores</span>
        </div>
        <Sparkline seed={2002} color="#16a34a" inverted />
      </div>

      {/* SOP Breaches */}
      <div className={styles.card}>
        <div className={styles.icon}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        </div>
        <div className={styles.label}>SOP Breaches</div>
        <div className={styles.valRow}>
          {sopLoading
            ? <Skeleton width={50} height={28} />
            : <div className={styles.value}>{sopOk ? sopCount : '—'}</div>}
        </div>
        <Sparkline seed={3003} color="#dc2626" />
      </div>

      {/* Store Operations */}
      <div className={styles.card}>
        <div className={styles.icon}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
        </div>
        <div className={styles.label}>Store Operations</div>
        <div className={styles.opsRow}>
          <div className={styles.opsItem}>
            <div className={styles.opsVal}>{avgOpen}</div>
            <div className={styles.opsLbl}>Avg. Open</div>
          </div>
          <div className={styles.opsSep}>·</div>
          <div className={styles.opsItem}>
            <div className={styles.opsVal}>{avgClose}</div>
            <div className={styles.opsLbl}>Avg. Close</div>
          </div>
        </div>
      </div>
    </div>
  );
}
